import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { goalsApi } from '../api/goals';
import {
  BrgGoalDto,
  CreateGoalRequest,
  UpdateGoalRequest,
  UpdateGoalStatusRequest,
  GoalsFilterParams,
} from '../types';

// Query hook for fetching goals
export function useGoals(params?: GoalsFilterParams) {
  return useQuery<BrgGoalDto[], Error>({
    queryKey: ['goals', params?.brgId, params?.fiscalYear, params?.pillar],
    queryFn: () => goalsApi.list(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// Query hook for fetching a single goal by ID
export function useGoalDetail(goalId: string | null) {
  return useQuery<BrgGoalDto, Error>({
    queryKey: ['goal', goalId],
    queryFn: () => (goalId ? goalsApi.getById(goalId) : Promise.reject(new Error('No goal ID'))),
    enabled: !!goalId,
  });
}

// Mutations hook
export function useGoalMutations() {
  const queryClient = useQueryClient();

  const create = useMutation<BrgGoalDto, Error, CreateGoalRequest>({
    mutationFn: (data) => goalsApi.create(data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['goals'] });
    },
  });

  const createMultiple = useMutation<BrgGoalDto[], Error, CreateGoalRequest[]>({
    mutationFn: (data) => goalsApi.createMultiple(data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['goals'] });
    },
  });

  const update = useMutation<
    BrgGoalDto,
    Error,
    { id: string; data: UpdateGoalRequest }
  >({
    mutationFn: ({ id, data }) => goalsApi.update(id, data),
    onSuccess: (_, { id }) => {
      void queryClient.invalidateQueries({ queryKey: ['goals'] });
      void queryClient.invalidateQueries({ queryKey: ['goal', id] });
    },
  });

  const updateStatus = useMutation<
    BrgGoalDto,
    Error,
    { id: string; data: UpdateGoalStatusRequest }
  >({
    mutationFn: ({ id, data }) => goalsApi.updateStatus(id, data),
    onSuccess: (_, { id }) => {
      void queryClient.invalidateQueries({ queryKey: ['goals'] });
      void queryClient.invalidateQueries({ queryKey: ['goal', id] });
    },
  });

  const deleteGoal = useMutation<void, Error, string>({
    mutationFn: (id) => goalsApi.delete(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['goals'] });
    },
  });

  return {
    create,
    createMultiple,
    update,
    updateStatus,
    delete: deleteGoal,
  };
}
